<template>
  <LayupSpinner v-if="isLoading" :size="10" />
  <div v-show="!isLoading">
    <div>
      <h1 class="font-semibold text-xl text-grey-light-900">Organization</h1>
      <p class="text-grey-light-600 text-sm">
        Get emails to find out what's going on when you're not online. You can turn them off anytime.
      </p>
    </div>
    <SectionDivider class="my-4" />
    <form @submit.prevent="onSubmit">
      <div class="grid lg:grid-cols-5 gap-4 md:grid-cols-5 sm:grid-cols-1">
        <div class="col-span-2">
          <h5 class="font-semibold text-grey-light-900">Company profile</h5>
          <p class="text-grey-light-600 text-sm">Allow users to pay instalments on a weekly basis.</p>
        </div>
        <div class="col-span-2">
          <div class="flex flex-col gap-4">
            <FormField v-slot="{ componentField }" name="companyProfile.name">
              <FormItem>
                <FormLabel>Company Name</FormLabel>
                <FormControl>
                  <Input type="text" placeholder="Company Name" v-bind="componentField" />
                </FormControl>
                <FormMessage />
              </FormItem>
            </FormField>
            <FormField v-slot="{ componentField }" name="companyProfile.tradingName">
              <FormItem>
                <FormLabel>Trading Name</FormLabel>
                <FormControl>
                  <Input type="text" placeholder="Trading Name" v-bind="componentField" />
                </FormControl>
                <FormMessage />
              </FormItem>
            </FormField>
            <div class="grid grid-cols-2 gap-4">
              <div class="col-span-1">
                <FormField v-slot="{ componentField }" name="companyProfile.vatNo">
                  <FormItem>
                    <FormLabel>VAT No.</FormLabel>
                    <FormControl>
                      <Input
                        type="text"
                        placeholder="VAT No."
                        :disabled="shouldDisableFieldUserNotAdmin"
                        v-bind="componentField"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                </FormField>
              </div>
              <div class="col-span-1">
                <FormField v-slot="{ componentField }" name="companyProfile.registeredEntity">
                  <FormItem>
                    <FormLabel>Registration Number</FormLabel>
                    <FormControl>
                      <Input type="text" placeholder="Registration Number" v-bind="componentField" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                </FormField>
              </div>
            </div>
            <GoogleAddressLookup
              label="Company Address"
              :address="address"
              :shouldDisableFieldUserNotAdmin="shouldDisableFieldUserNotAdmin"
              @update:places-found="onPlaceFound"
            />
          </div>
        </div>
      </div>
      <SectionDivider class="my-4" />
      <div class="grid lg:grid-cols-5 gap-4 md:grid-cols-5 sm:grid-cols-1">
        <div class="col-span-2">
          <h5 class="font-semibold text-grey-light-900">Contact details for customers</h5>
          <p class="text-grey-light-600 text-sm">For support unrelated to using LayUp as the payment method.</p>
        </div>
        <div class="col-span-2">
          <div class="flex flex-col gap-4">
            <FormField v-slot="{ componentField }" name="contactDetails.supportMobile">
              <FormItem>
                <FormLabel>Phone Number</FormLabel>
                <FormControl>
                  <Input type="text" placeholder="Phone Number" v-bind="componentField" />
                </FormControl>
                <FormMessage />
              </FormItem>
            </FormField>
            <FormField v-slot="{ componentField }" name="contactDetails.supportEmail">
              <FormItem>
                <FormLabel>Email</FormLabel>
                <FormControl>
                  <Input type="text" placeholder="Email" v-bind="componentField" />
                </FormControl>
                <FormMessage />
              </FormItem>
            </FormField>
          </div>
        </div>
      </div>
      <SectionDivider class="my-4" />
      <div class="grid lg:grid-cols-5 gap-4 md:grid-cols-5 sm:grid-cols-1">
        <div class="col-span-2">
          <h5 class="font-semibold text-grey-light-900">Terms & conditions</h5>
          <p class="text-grey-light-600 text-sm">
            Agreed to by your customers at checkout and included in email and SMS notifications.
          </p>
        </div>
        <div class="col-span-2">
          <div class="flex flex-col gap-4">
            <div class="space-y-2">
              <LabelComponent>Terms & Conditions</LabelComponent>
              <Input v-model="merchantTermsLink" readonly disabled type="text" placeholder="Terms & Conditions" />
              <Button type="button" @click="onViewTerms" variant="outline">View</Button>
            </div>
            <div>
              <div class="flex gap-2 items-center">
                <SwitchComponent :model-value="uploadViaPdf" @update:model-value="toggleTsAndCs('pdf')" />
                <LabelComponent>Upload terms and conditions. (PDF)</LabelComponent>
              </div>
              <div class="space-y-2 mt-4" v-if="!uploadingPdf">
                <Button type="button" variant="outline" v-if="uploadViaPdf" @click="triggerFileInput">
                  <ArrowUpToLine class="h-4 w-4" />
                  Click here to upload
                </Button>
                <p v-if="uploadPdfError" class="text-sm font-medium text-destructive">
                  {{ uploadPdfError }}
                </p>
                <input type="file" ref="fileInput" class="hidden" accept="application/pdf" @change="onFileChosen" />
              </div>
              <div class="mt-4 flex justify-start" v-if="uploadingPdf">
                <LayupSpinner />
              </div>
            </div>
            <div>
              <div class="flex gap-2 items-center">
                <SwitchComponent :model-value="uploadViaUrl" @update:model-value="toggleTsAndCs('url')" />
                <LabelComponent>Enter URL to your terms and conditions (http:// or https://).</LabelComponent>
              </div>
              <div class="mt-4 space-y-2" v-if="uploadViaUrl">
                <Input
                  v-model="uploadTermsUrl"
                  @input="onAddTermsUrl"
                  placeholder="https://"
                  :disabled="!uploadViaUrl"
                />
                <p v-if="uploadUrlError" class="text-sm font-medium text-destructive">
                  {{ uploadUrlError }}
                </p>
              </div>
            </div>
            <div>
              <Button type="button" @click="onDownloadTermsTemplate" variant="outline" class="mt-4">
                <ArrowDownToLine class="h-4 w-4" />
                Terms & Conditions Template
              </Button>
            </div>
          </div>
        </div>
      </div>
      <SectionDivider class="my-4" />
      <div class="grid lg:grid-cols-5 gap-4 md:grid-cols-5 sm:grid-cols-1">
        <div class="col-span-2">
          <h5 class="font-semibold text-grey-light-900">Settlements</h5>
        </div>
        <div class="col-span-2">
          <div class="flex flex-col gap-4">
            <FormField v-slot="{ componentField }" name="billing.settlementBatchReferencePrefix">
              <FormItem>
                <FormLabel>Settlement Reference Prefix</FormLabel>
                <FormControl>
                  <Input type="text" placeholder="Settlement Reference Prefix" v-bind="componentField" />
                </FormControl>
                <FormMessage />
              </FormItem>
            </FormField>
          </div>
        </div>
      </div>
      <SectionDivider class="my-4" />
      <div class="flex justify-end gap-4 mt-4 mr-8">
        <Button class="w-auto" type="submit">Save</Button>
      </div>
    </form>
  </div>
</template>

<script setup lang="ts">
import GoogleAddressLookup from '@/components/common/GoogleAddressLookup.vue';
import LayupSpinner from '@/components/common/LayupSpinner.vue';
import SectionDivider from '@/components/common/SectionDivider.vue';
import { Button } from '@/components/ui/button';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import LabelComponent from '@/components/ui/label/LabelComponent.vue';
import SwitchComponent from '@/components/ui/switch/SwitchComponent.vue';
import { CLOUDINARY_NAME, CLOUDINARY_UPLOAD_PRESET } from '@/config';
import { NetworkError } from '@/errors';
import { useMerchantStore } from '@/stores/merchants';
import { useUserStore } from '@/stores/users';
import { isValidEmail, isValidMobile, parseMobile } from '@/utils/form.utils';
import extractAddressComponents from '@/utils/google.utils';
import { debounce } from '@/utils/lowDash';
import { addMetric, createException, createWarning } from '@/utils/monitoring.utils';
import { toTypedSchema } from '@vee-validate/zod';
import axios from 'axios';
import { ArrowDownToLine, ArrowUpToLine } from 'lucide-vue-next';
import { storeToRefs } from 'pinia';
import { useForm } from 'vee-validate';
import { computed, ref, watch } from 'vue';
import { useToast } from 'vue-toast-notification';
import * as z from 'zod';
import type { ModelBillingAddress, ModelMerchantResponse } from '@layup/api-sdk';
import type { OrganisationSettings } from '../utils';

const props = defineProps<{
  settings: OrganisationSettings;
  merchantId: string;
}>();

const organizationFormSchema = toTypedSchema(
  z.object({
    companyProfile: z.object({
      name: z.string().min(2).max(50),
      tradingName: z.string().max(50).optional(),
      vatNo: z.string(),
      registeredEntity: z.string().optional(),
    }),
    contactDetails: z.object({
      supportMobile: z
        .string()
        .transform((val) => {
          const parsedMobile = parseMobile(val);
          form.setFieldValue('contactDetails.supportMobile', parsedMobile);

          return parsedMobile;
        })
        .refine((val) => isValidMobile(val), {
          message: 'Invalid mobile number',
        }),
      supportEmail: z
        .string()
        .email('Invalid email format')
        .refine((val) => isValidEmail(val), {
          message: 'Invalid email address',
        }),
    }),
    billing: z.object({
      settlementBatchReferencePrefix: z.string().max(50).optional(),
    }),
  }),
);

const emit = defineEmits(['save:merchant']);

const toast = useToast();
const userStore = useUserStore();
const merchantStore = useMerchantStore();
const { user } = storeToRefs(userStore);

const uploadViaPdf = ref(false);
const uploadViaUrl = ref(false);
const merchantTermsLink = ref('');
const uploadTermsUrl = ref('');
const uploadingPdf = ref(false);
const isLoading = ref(false);
const defaultTermsAndConditionsUrl = 'https://bit.ly/3CdNcgS';
const fileInput = ref<HTMLInputElement | null>(null);
const uploadPdfError = ref<string | null>(null);
const uploadUrlError = ref<string | null>(null);
const address = ref<string>('');
const addressParts = ref<ModelBillingAddress>({
  streetAddress: '',
  city: '',
  province: '',
  country: '',
  postalCode: '',
});

const shouldDisableFieldUserNotAdmin = computed(() => {
  return !user.value?.merchantRole || user.value.merchantRole !== 'ADMIN';
});

const form = useForm({
  validationSchema: organizationFormSchema,
});

watch(
  () => props.settings,
  (newSettings) => {
    if (!newSettings) {
      isLoading.value = false;
      return;
    }

    isLoading.value = true;

    const addressSections = [
      newSettings?.billingAddress?.streetAddress,
      newSettings?.billingAddress?.city,
      newSettings?.billingAddress?.province,
      newSettings?.billingAddress?.country,
      newSettings?.billingAddress?.postalCode,
    ];

    form.setValues({
      companyProfile: {
        name: newSettings.name || '',
        tradingName: newSettings.tradingName || '',
        vatNo: newSettings.vatNo || '',
        registeredEntity: newSettings.registeredEntity || '',
      },
      contactDetails: {
        supportMobile: newSettings.supportMobile || '',
        supportEmail: newSettings.supportEmail || '',
      },
      billing: {
        settlementBatchReferencePrefix: newSettings.settlementBatchReferencePrefix || '',
      },
    });

    // Setting refs not part of the form schema
    addressParts.value = {
      streetAddress: newSettings?.billingAddress?.streetAddress || '',
      city: newSettings?.billingAddress?.city || '',
      province: newSettings?.billingAddress?.province || '',
      country: newSettings?.billingAddress?.country || '',
      postalCode: newSettings?.billingAddress?.postalCode || '',
    };
    address.value = addressSections.filter(Boolean).join(', ');
    merchantTermsLink.value = newSettings.merchantTermsLink || '';

    // Silent validation won't display errors till user submits or touches a field
    form.validate({ mode: 'silent' });
    isLoading.value = false;
  },
  { immediate: true, deep: true },
);

const toggleTsAndCs = (option: 'pdf' | 'url') => {
  if (option === 'pdf') {
    uploadViaPdf.value = !uploadViaPdf.value;
    uploadViaUrl.value = false;
  } else {
    uploadViaPdf.value = false;
    uploadViaUrl.value = !uploadViaUrl.value;
  }
};

const triggerFileInput = () => {
  fileInput.value?.click();
};

const onViewTerms = () => {
  if (merchantTermsLink.value !== '') {
    window.open(merchantTermsLink.value, '_blank');
  }
};

const onDownloadTermsTemplate = () => {
  window.open(defaultTermsAndConditionsUrl, '_blank');
};

const validateFile = (file: File) => {
  const maxFileSize = 5 * 1024 * 1024; // 5MB

  if (file.type !== 'application/pdf') {
    uploadPdfError.value = 'Invalid file type. Please upload a PDF file.';
    return false;
  }

  if (file.size > maxFileSize) {
    uploadPdfError.value = 'File size exceeds 5MB limit.';
    return false;
  }

  uploadPdfError.value = null;
  return true;
};

const uploadFile = async (file: File) => {
  const cloudName = CLOUDINARY_NAME;
  const uploadPreset = CLOUDINARY_UPLOAD_PRESET;
  const apiEndpoint = `https://api.cloudinary.com/v1_1/${cloudName}/upload`;

  const formData = new FormData();
  formData.append('upload_preset', uploadPreset);
  formData.append('file', file);

  try {
    await axios
      .post(apiEndpoint, formData, {
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        // Had to set this otherwise the API call would fail with a CORS error
        withCredentials: false,
      })
      .then((response) => {
        addMetric('settings-organization', { status: 'success', channel: 'merchant', method: 'uploadFile' });
        const secureUrl = response.data.secure_url;
        merchantTermsLink.value = secureUrl.replace('/upload/', '/upload/fl_attachment/');

        toast.success('File uploaded successfully. Please save changes below.', { position: 'top' });
      });
  } catch (error) {
    if (error instanceof NetworkError) {
      createWarning(error);
      addMetric('settings-organization', { status: 'network_error', channel: 'merchant', method: 'uploadFile' });
    } else {
      createException(error);
      addMetric('settings-organization', { status: 'error', channel: 'merchant', method: 'uploadFile' });
    }
    toast.error('Failed to upload file. Please try again.', { position: 'top' });
  } finally {
    uploadingPdf.value = false;
  }
};

const onFileChosen = (event: Event) => {
  const target = event.target as HTMLInputElement;
  const files = target.files;

  if (!files || files.length === 0) return;

  const file = files[0];

  if (!validateFile(file)) return;

  uploadingPdf.value = true;
  uploadFile(file);
};

const validURL = (url: string) => {
  const pattern = new RegExp(
    '^(http:\\/\\/)' + // protocol
      '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // domain name
      '((\\d{1,3}\\.){3}\\d{1,3}))' + // OR ip (v4) address
      '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' + // port and path
      '(\\?[;&a-z\\d%_.~+=-]*)?' + // query string
      '(\\#[-a-z\\d_]*)?$',
    'i',
  ); // fragment locator
  return !!pattern.test(url);
};
const isValidHttpsUrl = (url: string) => {
  const pattern = new RegExp(
    '^(https:\\/\\/)' + // protocol
      '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // domain name
      '((\\d{1,3}\\.){3}\\d{1,3}))' + // OR ip (v4) address
      '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' + // port and path
      '(\\?[;&a-z\\d%_.~+=-]*)?' + // query string
      '(\\#[-a-z\\d_]*)?$',
    'i',
  ); // fragment locator
  return !!pattern.test(url);
};

const debouncedOnChange = debounce((url: string) => {
  if (validURL(url) || isValidHttpsUrl(url)) {
    merchantTermsLink.value = url;
    uploadUrlError.value = null;
  } else {
    uploadUrlError.value = 'Invalid ULR. Please enter a valid URL.';
  }
}, 350);

const onAddTermsUrl = (event: any) => {
  debouncedOnChange(event.target.value);
};

const onPlaceFound = (place: google.maps.places.AddressComponent[]) => {
  if (!place) {
    toast.error('Failed to get address details', { position: 'top' });
    return;
  }
  addressParts.value = extractAddressComponents(place);
};

const onSubmit = async () => {
  isLoading.value = true;
  const { values, errors, valid } = await form.validate();

  // Need to manually check errors because for some reason vee-validate doesn't set errors on the form when submitted even if valid is false
  if (Object.keys(errors).length > 0) {
    Object.entries(errors).forEach(([key, value]) => {
      form.setFieldError(key as keyof typeof errors, value);
    });
    isLoading.value = false;
    return;
  }

  if (valid) {
    try {
      if (props.merchantId === '') {
        toast.error('Merchant ID is not set. Please try reloading the page', { position: 'top' });
        throw new Error('Merchant ID is required.');
      }

      const settings: ModelMerchantResponse = {
        ...props.settings,
        ...values?.companyProfile,
        ...values?.billing,
        supportMobile: values?.contactDetails?.supportMobile.replace(/\s+/g, ''),
        supportEmail: values?.contactDetails?.supportEmail,
        merchantTermsLink: merchantTermsLink.value,
        billingAddress: { ...addressParts.value },
      };

      const response = await merchantStore.saveMerchantSettings({
        id: props.merchantId,
        modelMerchantRequest: settings,
      });
      emit('save:merchant', response);

      addMetric('settings-page-organization', { status: 'success', channel: 'merchant', method: 'saveMerchant' });
    } catch (error) {
      if (error instanceof NetworkError) {
        createWarning(error);
        addMetric('settings-page-organization', {
          status: 'network_error',
          channel: 'merchant',
          method: 'saveMerchant',
        });
      } else {
        createException(error);
        addMetric('settings-page-organization', { status: 'error', channel: 'merchant', method: 'saveMerchant' });
      }
    }
  }
};
</script>
